import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { useForm } from 'react-hook-form'

// Images
import Email from '../../assets/images/email.svg'
import User from '../../assets/images/user.svg'

// Components
import { EditIcon } from '../EditIcon'
import { Modal } from '../Modal'
import { PhoneNumberInput } from '../PhoneNumberInput'
import { PurchaseLicensesModal } from '../PurchaseLicensesModal'
import { TextInput } from '../TextInput'

// Service
import { updateExhibitor } from '../../services/exhibitors.service'

// Hooks
import usePurchaseTransaction from '../../hooks/PurchaseTransaction'

// Utils
import { getChargeData, getPricingData } from '../PurchaseLicensesModal/helpers'
import { toast } from '../../utils/helpers'

/**
 *
 * ExhibitorOnboarding
 *
 * The setup an exhibitor completes before their event goes active: confirm the event
 * contact, then either activate the free licenses they were given or purchase some.
 * Marking the exhibitor onboarded is what flips the event out of the Inactive state
 * the dashboard and event home show (EXEC-1596).
 *
 * This lives beside the event rather than on the dashboard so that a user who clicks
 * into an inactive event can finish setup where they landed. `event` must be the full
 * event record — the pricing windows and the exhibitor's contact details are both read
 * off it, and neither is present on a dashboard list row.
 */
const ExhibitorOnboarding = ({ event, onCancel, onComplete }) => {
  // Hooks
  const { loadingPurchase, submitPurchase, repeatPurchaseDialog } = usePurchaseTransaction()

  // State
  const [loading, setLoading] = useState(false)
  const [showContactModal, setShowContactModal] = useState(true)
  const [showFreeLicensesModal, setShowFreeLicensesModal] = useState(false)
  const [showLicensesModal, setShowLicensesModal] = useState(false)
  // Counts come back with the contact save; `numAllotedFreeLicenses` folds in the
  // event's default allotment, which the exhibitor's own override field does not.
  const [numFreeLicenses, setNumFreeLicenses] = useState(0)

  const eventExhibitor = event?.eventExhibitor

  const handleErrors = (m) => toast(m, 'error')
  const handleSuccess = (m) => toast(m, 'success')

  const {
    control,
    handleSubmit,
    formState: { errors },
    register,
    reset,
  } = useForm({
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
    },
  })

  useEffect(() => {
    if (eventExhibitor) {
      reset({
        firstName: eventExhibitor.contactFirstName ?? '',
        lastName: eventExhibitor.contactLastName ?? '',
        email: eventExhibitor.contactEmail ?? '',
        phone: eventExhibitor.contactPhoneNumber ?? '',
      })
    }
  }, [eventExhibitor])

  /**
   * Marks the exhibitor as having completed onboarding for the event, which is what
   * makes the event active.
   * @returns the updated exhibitor, or null if the request failed
   */
  const completeOnboarding = async () => {
    const updated = await updateExhibitor(
      event.id,
      {
        id: eventExhibitor.id,
        hasCompletedOnboarding: true,
      },
      handleErrors,
      setLoading,
      () => {},
    )

    if (updated) {
      handleSuccess(`Successfully onboarded for ${event.name}.`)
      onComplete(updated)
    }

    return updated
  }

  /**
   * Returns to the contact step. Esc and backdrop dismissal route here too, so a stray
   * keypress reopens the step behind rather than closing the flow onto an empty page.
   */
  const backToContact = () => {
    setShowFreeLicensesModal(false)
    setShowContactModal(true)
  }

  /**
   * Saves the event contact, then routes to whichever license step the exhibitor
   * still owes: activating free licenses, buying some, or neither.
   * @param {object} data
   */
  const onSubmitContactInformation = async (data) => {
    const updatedExhibitor = await updateExhibitor(
      event.id,
      {
        id: eventExhibitor.id,
        contactFirstName: data.firstName,
        contactLastName: data.lastName,
        contactEmail: data.email,
        // The outgoing transform snake_cases this to `contact_phone_number`, the actual
        // field. It used to send `contactPhone` — `contact_phone` is not a field, so DRF
        // dropped it and the number collected here was never stored (EXEC-1596).
        //
        // The field takes null and blank alike; null is sent for an untouched form so the
        // stored value reads as "no number" rather than an empty one.
        contactPhoneNumber: data.phone || null,
      },
      handleErrors,
      setLoading,
      () => {},
    )

    if (!updatedExhibitor) return

    handleSuccess('Contact information set.')
    setShowContactModal(false)

    const { licenseCounts } = updatedExhibitor

    // Free licenses they have been given but not yet activated
    if (licenseCounts.numAllotedFreeLicenses > 0 && licenseCounts.numValidFreeLicenses === 0) {
      setNumFreeLicenses(licenseCounts.numAllotedFreeLicenses)
      setShowFreeLicensesModal(true)
    }
    // Nothing free and nothing bought yet, so they have licenses to purchase
    else if (
      licenseCounts.numValidPaidLicenses === 0 &&
      licenseCounts.numValidFreeLicenses === 0
    ) {
      setShowLicensesModal(true)
    }
    // Already holding licenses — setup is done
    else {
      await completeOnboarding()
    }
  }

  /**
   * Handles submitting the purchase of licenses for the event.
   * @param {object} data
   */
  const onSubmitLicensePurchase = async (data) => {
    // A hardcoded zero purchased count, which is the only value this step can reach:
    // it opens only when the exhibitor has bought nothing and been given nothing, so
    // they pay the first-license price. An exhibitor with a free allotment goes to the
    // free-licenses modal above instead, and pays that same price whenever they buy.
    const chargeData = await getChargeData(data, getPricingData(event), 0)

    // Onboarding is only complete once the purchase has actually gone through, so this
    // hangs off the success callback. A purchase that repeats one just made resolves
    // without succeeding — it stops here and waits on the confirmation instead.
    await submitPurchase(event.id, eventExhibitor.id, chargeData, handleErrors, async () => {
      setShowLicensesModal(false)
      await completeOnboarding()
    })
  }

  if (!eventExhibitor) return null

  return (
    <>
      {showContactModal && (
        <Modal
          actions={[
            {
              type: 'cancel',
              label: 'Cancel',
              onClick: onCancel,
            },
            {
              type: 'submit',
              label: 'Save',
              onClick: handleSubmit(onSubmitContactInformation),
            },
          ]}
          icon={<EditIcon className="h-5 fill-white sm:h-6" />}
          content={
            <div className="mt-3 flex flex-col space-y-4 text-center sm:mt-5">
              <div className="flex flex-col sm:flex-row sm:space-x-2">
                <TextInput
                  className="w-full rounded-2xl border-gray-550 py-2.5 pl-9 placeholder:font-normal placeholder:text-gray-600 focus-within:border-purple"
                  icon={<img alt="User" className="ml-2 h-4" src={User} />}
                  error={errors.firstName && 'This field is required'}
                  fullWidth
                  id="firstName"
                  inputStyles="rounded-none rounded-t-md font-nunito"
                  name="firstName"
                  nunito
                  label="First Name"
                  placeholder="First Name"
                  {...register('firstName', { required: true })}
                />
                <TextInput
                  className="w-full rounded-2xl border-gray-550 py-2.5 pl-9 pr-4 placeholder:font-normal placeholder:text-gray-600 focus-within:border-purple"
                  icon={<img alt="User" className="ml-2 h-4" src={User} />}
                  error={errors.lastName && 'This field is required'}
                  fullWidth
                  id="lastName"
                  inputStyles="rounded-none rounded-t-md font-nunito"
                  name="lastName"
                  nunito
                  label="Last Name"
                  placeholder="Last Name"
                  {...register('lastName', { required: true })}
                />
              </div>
              <TextInput
                className="w-full rounded-2xl border-gray-550 py-2.5 pl-9 pr-4 placeholder:font-normal placeholder:text-gray-600 focus-within:border-purple"
                icon={<img alt="Email" className="ml-1.5 h-4" src={Email} />}
                error={errors.email && 'This field is required'}
                fullWidth
                id="email"
                inputStyles="rounded-none rounded-t-md font-nunito"
                name="email"
                nunito
                label="Email"
                placeholder="Email"
                {...register('email', { required: true })}
              />
              <PhoneNumberInput
                control={control}
                disabled={loading}
                error={errors.phone}
                label="Phone"
                name="phone"
              />
            </div>
          }
          loading={loading}
          open={showContactModal}
          title="Confirm Event Contact"
        />
      )}

      {showFreeLicensesModal && (
        <Modal
          actions={[
            {
              type: 'cancel',
              label: 'Back',
              onClick: backToContact,
            },
            {
              type: 'submit',
              label: 'Activate',
              onClick: async () => {
                setShowFreeLicensesModal(false)
                await completeOnboarding()
              },
            },
          ]}
          icon={<EditIcon className="h-5 fill-white sm:h-6" />}
          content={
            <div className="mt-3 sm:mt-5">
              <div>
                <span className="text-sm text-gray-600">
                  Congrats! You have received <strong>{`${numFreeLicenses} FREE`}</strong> lead
                  retrieval licenses. You may purchase additional licenses or rental devices in
                  your Exhibitor Portal. Click &quot;Activate&quot; below to start building out
                  your Lead Retrieval App!
                </span>
              </div>
            </div>
          }
          loading={loading}
          open={showFreeLicensesModal}
          setOpen={backToContact}
          title="Activate License(s)"
        />
      )}

      {showLicensesModal && (
        <PurchaseLicensesModal
          event={event}
          loading={loadingPurchase}
          onSubmit={onSubmitLicensePurchase}
          showModal={showLicensesModal}
          // Dismissing the purchase step is leaving setup, so it exits the way Cancel
          // does. The successful-purchase path closes via the state setter directly.
          setShowModal={onCancel}
        />
      )}

      {repeatPurchaseDialog}
    </>
  )
}

ExhibitorOnboarding.propTypes = {
  // The full event record, expanded with `eventExhibitor`
  event: PropTypes.object.isRequired,
  onCancel: PropTypes.func.isRequired,
  onComplete: PropTypes.func.isRequired,
}

export default ExhibitorOnboarding
